import {
  AirHandlerProductType,
  CondenserProductType,
  SystemOptionCondenserType,
  ValidSystemType
} from 'data/types';
import { getZoneNumberById } from 'utils/systems';

const getAirHandlerProductIdForZone = (
  system: ValidSystemType,
  zoneId: string
): number | null | undefined => {
  const zoneNumber = getZoneNumberById(system, zoneId);
  return system[`airHandler${zoneNumber}ProductId` as keyof ValidSystemType] as
    | number
    | null
    | undefined;
};

const matchesLockedZones = (
  system: ValidSystemType,
  condenser: SystemOptionCondenserType,
  lockedZones: string[],
  ignoredZoneId?: string
): boolean =>
  lockedZones
    .filter((zoneId) => zoneId !== ignoredZoneId)
    .every((zoneId) => {
      const lockedProductId = condenser.zones.find(
        (zone) => zone.uniqueId === zoneId
      )?.selectedProduct?.productId;
      return getAirHandlerProductIdForZone(system, zoneId) === lockedProductId;
    });

const matchesCondenserLock = (
  system: ValidSystemType,
  condenser: SystemOptionCondenserType,
  condenserLocked: boolean
): boolean =>
  !condenserLocked ||
  system.condenserProductId === condenser.selectedProduct?.productId;

export function getValidCondenserSwapProducts(
  validSystems: ValidSystemType[],
  condenser: SystemOptionCondenserType,
  lockedZones: string[],
  condenserProducts: CondenserProductType[]
): CondenserProductType[] {
  const validProductIds = validSystems
    .filter((system) => matchesLockedZones(system, condenser, lockedZones))
    .map((system) => system.condenserProductId)
    .filter((id) => id !== condenser.selectedProduct?.productId);
  return condenserProducts.filter((product) =>
    validProductIds.includes(product.productId)
  );
}

export function getValidAirHandlerSwapProductsByZone(
  validSystems: ValidSystemType[],
  condenser: SystemOptionCondenserType,
  condenserLocked: boolean,
  lockedZones: string[],
  airHandlerProducts: AirHandlerProductType[]
): Record<string, AirHandlerProductType[]> {
  const result: Record<string, AirHandlerProductType[]> = {};
  condenser.zones
    .filter((zone) => zone.selectedProduct != null)
    .forEach((zone) => {
      const validProductIds = validSystems
        .filter((system) =>
          matchesCondenserLock(system, condenser, condenserLocked)
        )
        .filter((system) =>
          matchesLockedZones(system, condenser, lockedZones, zone.uniqueId)
        )
        .map((system) => getAirHandlerProductIdForZone(system, zone.uniqueId))
        .filter((id) => id !== zone.selectedProduct?.productId);
      result[zone.uniqueId] = airHandlerProducts.filter((product) =>
        validProductIds.includes(product.productId)
      );
    });
  return result;
}
